/*
 * When the installed app looks for a new release of Beacon, and what it does once one is found.
 * These are the rules alone, kept apart from the service worker and the browser so each can be
 * checked without either (`updates.ts` runs them).
 *
 * A release reaches a tab as a new service worker that waits until the Member takes it. Taking it
 * in one tab moves every open tab onto it, and each of those tabs then still runs the code it
 * loaded with, so it reloads too, unless something in it holds unsaved work: a deploy never throws
 * away what a Member has written.
 */

/** How long after the last look a tab coming back into view looks again: often enough to matter. */
export const checkOnReturnAfter = 5 * 60 * 1000

/** How often a tab in view looks for a release, however long it stays open. */
export const checkEvery = 30 * 60 * 1000

/**
 * What prompts a look for a release: the tab coming back into view (`return`), the half-hourly
 * look while it stays in view (`interval`), the network coming back (`online`), or a page's code
 * failing to load (`chunk`), which most often means a deploy replaced it.
 */
export type CheckReason = "return" | "interval" | "online" | "chunk"

/**
 * Whether to look for a release now. A tab coming back looks at most every five minutes, so
 * switching between apps doesn't fetch `sw.js` each time; the half-hourly look waits while the tab
 * is out of view; the network returning and a failed chunk always look.
 */
export function checkDue({
  reason,
  lastCheckedAt,
  now,
  visible,
}: {
  reason: CheckReason
  /** When this tab last looked, in ms, or never. */
  lastCheckedAt: number | undefined
  now: number
  visible: boolean
}) {
  switch (reason) {
    case "return":
      return lastCheckedAt === undefined || now - lastCheckedAt >= checkOnReturnAfter
    case "interval":
      return visible
    case "online":
    case "chunk":
      return true
  }
}

/**
 * Who moved this tab onto a new service worker: the Member, pressing Reload in it (`here`);
 * another tab, where they pressed it (`elsewhere`); or nobody, when the very first worker takes
 * the page that installed it (`first-install`), which is the same release the page already runs.
 */
export type TakenBy = "here" | "elsewhere" | "first-install"

/**
 * What a tab does once a new worker controls it: `reload` onto the release, `hold` (show that
 * Beacon was updated in another tab, and wait for the Member's Reload), or `stay`, as it is. Reload
 * pressed here has already asked about unsaved work (`reloadPressed`), so it reloads.
 */
export function onTaken({ by, unsaved }: { by: TakenBy; unsaved: boolean }) {
  if (by === "first-install") return "stay"
  if (by === "here") return "reload"
  return unsaved ? "hold" : "reload"
}

/**
 * What Reload does, wherever it is pressed (the notice, the avatar's drawer, the account menu, the
 * palette, a page that says Beacon has been updated): with unsaved work, nothing but saying to save
 * or discard it first (`hold`); otherwise it takes the waiting release, or with none waiting (it
 * was taken in another tab, or the release was found by a failed chunk) reloads the page.
 */
export function reloadPressed({ unsaved, waiting }: { unsaved: boolean; waiting: boolean }) {
  if (unsaved) return "hold"
  return waiting ? "take" : "reload"
}

/**
 * What a launch does about a release that was found and left waiting before it, ahead of the first
 * paint, and what the session remembers of it (`tried`). Only a page a worker already controls has
 * one to take, and it takes it once a session: `take` it, with one quick reload from the cache and
 * nothing yet on screen to lose; `skip` it, when this session took one and it didn't stick, rather
 * than reloading again and again; or, with nothing waiting, `none`. Only `none` forgets that the
 * session tried, since by then the release it tried has been taken, and the next may be taken too.
 */
export function atLaunch({
  controlled,
  waiting,
  tried,
}: {
  controlled: boolean
  waiting: boolean
  tried: boolean
}): { does: "take" | "skip" | "none"; tried: boolean } {
  if (!controlled || !waiting) return { does: "none", tried: false }
  return { does: tried ? "skip" : "take", tried: true }
}

/**
 * Where this tab stands with releases: on the newest it knows of (`current`); with one waiting to
 * be taken (`ready`); or already behind one, its code older than the release that now serves it
 * (`behind`), because another tab took it or a page's code it asked for was gone. `held` is set
 * once Reload was pressed over unsaved work, until the release is taken.
 */
export type UpdateState =
  | { status: "current" }
  | { status: "ready"; held: boolean }
  | { status: "behind"; because: "other-tab" | "chunk"; held: boolean }

/** What the notice says for a state, or nothing, on the newest release. */
export function noticeOf(state: UpdateState): { title: string; description?: string } | undefined {
  if (state.status === "current") return undefined
  const title =
    state.status === "ready"
      ? "A new version of Beacon is ready"
      : state.because === "other-tab"
        ? "Beacon was updated in another tab"
        : "Beacon has been updated"
  return state.held
    ? { title, description: "Save or discard what you've written first, then reload." }
    : state.status === "behind"
      ? { title, description: "Reload to carry on in the new version." }
      : { title }
}
